import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import AccordionItem from "../../Components/UI-Controls/Accordian/Accordian";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";
import Logo from "../../../src/Assets/Images/Logo 29.44 X 5.12.png";
import Selector from "../../../src/Assets/Images/Selector 20 X 20.png";
import MainLoader from "../../Components/UI-Controls/MainLoader/MainLoader";
import JoditEditor from "jodit-react";
import "../../../src/JoditEditorStyle.css";
import {
  security_key,
  Url,
  financeConfigEditor,
  FINANCE_MEDIA_ADD_PATH,
} from "../../Services/Api";
import CustomBtn from "../../Components/UI-Controls/Buttons/Btn";
import { PiNotePencil } from "react-icons/pi";
import imageStatus from "../../../src/Assets/Images/StatusImg 20 x 20.png";
import imageVisibility from "../../../src/Assets/Images/Visibility 20 x 20.png";
import imagePublish from "../../../src/Assets/Images/Publish 20 x 20.png";
import { IoImageOutline } from "react-icons/io5";
import ImageGalleryPopup from "../../Components/UI-Controls/PopUp/ImageGalleryPapup/ImageGalleryPopup";
import { uploadImage } from "../../Services/functions";
import BottomToust from "../../Components/BottomToust/BottomToust";
import "../ECommerce/ECommerce.css";
import "../Blogs/Blogs.css";
import "../Page.css";

/* ════════════════════════════════════
   Constants
════════════════════════════════════ */
const FINANCING_API = `${Url}/api/v1/pages/financing-main`;
const FINANCE_MEDIA_ADD_API = `${Url}${FINANCE_MEDIA_ADD_PATH}`;

const EMPTY_FORM = { content: "" };

const EMPTY_META = {
  metaTitle: "",
  metaDescription: "",
  metaKeywords: "",
  canonicalUrl: "",
  robots: "",
  ogTitle: "",
  ogDescription: "",
  og_image: "",
  xTitle: "",
  xDescription: "",
  x_image: "",
  schemaMarkup: "",
};

const SEO_ACCORDION_TITLES = [
  "Search Appearance",
  "Open Graph (OG)",
  "X (Twitter) Card",
  "Schema Markup",
];

/* The GET "/" response shape can vary by controller – handle the common ones */
const extractPage = (resData) => {
  const p =
    resData?.data ??
    resData?.page ??
    resData?.financingPage ??
    resData?.financingMainPage ??
    resData;
  if (Array.isArray(p)) return p[0] || null;
  return p && p._id ? p : null;
};

const formatDate = (dateString) =>
  dateString ? new Date(dateString).toLocaleDateString() : "Not set";

const AddEditMainFinancingPage = () => {
  const editor = useRef(null);

  /* ── Jodit instance + cursor position, used when inserting gallery images ── */
  const joditInstanceRef = useRef(null);
  const selectionBookmarkRef = useRef(null);

  /* ── saved record from API (null = no page exists yet) ── */
  const [pageRecord, setPageRecord] = useState(null);

  /* ── mode: "create" | "view" | "edit" ── */
  const [mode, setMode] = useState("create");
  const isViewMode = mode === "view";
  const isEditMode = mode === "edit";

  /* ── loading / toast ── */
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  /* ── editor tab ── */
  const [selectedTab, setSelectedTab] = useState("Visual");

  /* ── SEO preview tab ── */
  const [selectedView, setSelectedView] = useState("Web");
  const [isMobileAnimating, setIsMobileAnimating] = useState(false);
  const [isWebAnimating, setIsWebAnimating] = useState(false);

  /* ── SEO accordions ── */
  const [accordionsOpen, setAccordionsOpen] = useState({});

  /* ── form ── */
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [metaData, setMetaData] = useState(EMPTY_META);

  /* ── images ── */
  const [pageImage, setPageImage] = useState(null); // full image object
  const [selectedOgImage, setSelectedOgImage] = useState(null);
  const [selectedXImage, setSelectedXImage] = useState(null);

  /* ── image gallery ── */
  const [modalView, setModalView] = useState(false);
  const [modaldata, setModalData] = useState([]);
  const [isUploaded, setIsUploaded] = useState(false);
  const [, setUploadedStates] = useState(null);
  const [imageType, setImageType] = useState("");
  const [imageSendPayload, setImageSendPayload] = useState({
    file: null,
    alt_text: "",
    title: "",
    description: "",
    link_url: "",
  });

  /* ════════════════════════════════════
     Toast
  ════════════════════════════════════ */
  const showToast = (msg) => {
    setToastMessage(msg);
    setShowMessage(true);
  };

  useEffect(() => {
    if (showMessage) {
      const t = setTimeout(() => setShowMessage(false), 3000);
      return () => clearTimeout(t);
    }
  }, [showMessage]);

  /* ════════════════════════════════════
     Populate form from a saved record
  ════════════════════════════════════ */
  const populateForm = useCallback((data) => {
    if (!data) {
      setFormData(EMPTY_FORM);
      setMetaData(EMPTY_META);
      setPageImage(null);
      setSelectedOgImage(null);
      setSelectedXImage(null);
      return;
    }
    const m = data.meta || {};
    setFormData({ content: data.content || "" });
    setMetaData({
      metaTitle: m.title || "",
      metaDescription: m.description || "",
      metaKeywords: m.keywords || "",
      canonicalUrl: m.canonical_url || "",
      robots: m.robots || "",
      ogTitle: m.og_title || "",
      ogDescription: m.og_description || "",
      og_image: m.og_image || "",
      xTitle: m.x_title || "",
      xDescription: m.x_description || "",
      x_image: m.x_image || "",
      schemaMarkup:
        m.schemaMarkup && Object.keys(m.schemaMarkup).length
          ? JSON.stringify(m.schemaMarkup, null, 2)
          : "",
    });
    setPageImage(data.image?.image_url ? data.image : null);
    setSelectedOgImage(m.og_image || null);
    setSelectedXImage(m.x_image || null);
  }, []);

  /* ════════════════════════════════════
     Load existing financing page
  ════════════════════════════════════ */
  const fetchFinancingPage = useCallback(async () => {
    setLoading(true);
    try {
      // Public GET – no auth headers
      const res = await axios.get(FINANCING_API);
      const page = extractPage(res.data);
      setPageRecord(page);
      populateForm(page);
      setMode(page ? "view" : "create");
    } catch (error) {
      // 404 = no page yet → empty create form
      if (error.response?.status !== 404) {
        console.error("Error loading financing page:", error);
        showToast("Failed to load financing page.");
      }
      setPageRecord(null);
      populateForm(null);
      setMode("create");
    } finally {
      setLoading(false);
    }
  }, [populateForm]);

  useEffect(() => {
    fetchFinancingPage();
  }, [fetchFinancingPage]);

  /* ════════════════════════════════════
     Image gallery API
  ════════════════════════════════════ */
  const getApi = async () => {
    try {
      const response = await axios.get(`${Url}/api/v1/media/pages/home/finance/get`);
      setModalData(response.data.homeFinanceSliders);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getApi();
  }, []);

  useEffect(() => {
    if (isUploaded) {
      getApi();
      setIsUploaded(false);
    }
  }, [isUploaded]);

  /* ════════════════════════════════════
     Input handlers
  ════════════════════════════════════ */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMetaChange = (e) => {
    const { name, value } = e.target;
    setMetaData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditorChange = (newContent) => {
    setFormData((prev) => ({ ...prev, content: newContent }));
  };

  /* ════════════════════════════════════
     Image gallery handlers
  ════════════════════════════════════ */
  const handleGalleryModalOpen = (clickType) => {
    setImageType(clickType);
    setModalView(true);
  };

  const handleGalleryModalClose = () => setModalView(false);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    const api = FINANCE_MEDIA_ADD_API;
    if (file) {
      setImageSendPayload((prev) => ({ ...prev, file }));
      setUploadedStates("loading");
      const payload = new FormData();
      payload.append("image", file);
      payload.append("alt_text", imageSendPayload.alt_text);
      payload.append("title", imageSendPayload.title);
      payload.append("description", imageSendPayload.description);
      payload.append("link_url", imageSendPayload.link_url);
      await uploadImage(payload, api, setUploadedStates);
    }
    setIsUploaded(true);
  };

  const handleImageSelect = (image) => {
    switch (imageType) {
      case "page-image":
        setPageImage(image);
        break;
      case "og-image":
        setMetaData((prev) => ({ ...prev, og_image: image.image_url }));
        setSelectedOgImage(image.image_url);
        break;
      case "x-image":
        setMetaData((prev) => ({ ...prev, x_image: image.image_url }));
        setSelectedXImage(image.image_url);
        break;
      case "editor-image": {
        const editorInstance = joditInstanceRef.current;
        const imageUrl = `${Url}${image.image_url}`;
        if (editorInstance) {
          editorInstance.selection.restore(selectionBookmarkRef.current);
          editorInstance.selection.insertHTML(
            `<img src="${imageUrl}" class="blog-content-image" alt="${
              image.alt_text || ""
            }" />`
          );
          setFormData((prev) => ({ ...prev, content: editorInstance.value }));
        }
        joditInstanceRef.current = null;
        selectionBookmarkRef.current = null;
        break;
      }
      default:
        break;
    }
    setModalView(false);
  };

  const cancelOgImage = () => {
    setSelectedOgImage(null);
    setMetaData((prev) => ({ ...prev, og_image: "" }));
  };
  const cancelXImage = () => {
    setSelectedXImage(null);
    setMetaData((prev) => ({ ...prev, x_image: "" }));
  };

  /* ════════════════════════════════════
     Jodit config — image button opens the media gallery
  ════════════════════════════════════ */
  const editorConfig = useMemo(
    () => ({
      ...financeConfigEditor,
      readonly: isViewMode,
      controls: {
        ...(financeConfigEditor.controls || {}),
        image: {
          ...(financeConfigEditor.controls?.image || {}),
          // Replaces Jodit's built-in Upload/URL popup with our gallery
          exec: (editorInstance) => {
            joditInstanceRef.current = editorInstance;
            selectionBookmarkRef.current = editorInstance.selection.save();
            handleGalleryModalOpen("editor-image");
          },
        },
      },
    }),
    [isViewMode]
  );

  /* ════════════════════════════════════
     SEO UI toggles
  ════════════════════════════════════ */
  const toggleAccordion = (index) =>
    setAccordionsOpen((prev) => ({ ...prev, [index]: !prev[index] }));

  const handleViewChange = (view) => {
    if (view === "Mobile") {
      setIsMobileAnimating(true);
      setTimeout(() => setIsMobileAnimating(false), 300);
    } else {
      setIsWebAnimating(true);
      setTimeout(() => setIsWebAnimating(false), 300);
    }
    setSelectedView(view);
  };

  /* ════════════════════════════════════
     Mode actions
  ════════════════════════════════════ */
  const handleEdit = () => setMode("edit");

  const handleCancel = () => {
    // Discard unsaved changes
    populateForm(pageRecord);
    setMode(pageRecord ? "view" : "create");
  };

  /* ════════════════════════════════════
     Save (POST when none exists, PUT otherwise)
  ════════════════════════════════════ */
  const handleSave = async () => {
    if (!formData.content.trim()) {
      showToast("Page content is required.");
      return;
    }

    let schemaObj = {};
    if (metaData.schemaMarkup.trim()) {
      try {
        schemaObj = JSON.parse(metaData.schemaMarkup);
      } catch {
        showToast("Schema Markup is not valid JSON.");
        return;
      }
    }

    const payload = {
      content: formData.content,
      image: pageImage?.image_url
        ? {
            image_url: pageImage.image_url,
            alt_text: pageImage.alt_text || "",
            title: pageImage.title || "",
            link_url: pageImage.link_url || "",
            description: pageImage.description || "",
          }
        : {},
      meta: {
        title: metaData.metaTitle,
        description: metaData.metaDescription,
        keywords: metaData.metaKeywords,
        canonical_url: metaData.canonicalUrl,
        robots: metaData.robots,
        og_title: metaData.ogTitle,
        og_description: metaData.ogDescription,
        og_image: metaData.og_image,
        x_title: metaData.xTitle,
        x_description: metaData.xDescription,
        x_image: metaData.x_image,
        schemaMarkup: schemaObj,
      },
    };

    const token = sessionStorage.getItem("authToken");
    if (!token) {
      showToast("You are not logged in. Please log in again.");
      return;
    }

    // Create / Edit both send the token AND the security key
    const headers = {
      Authorization: `Bearer ${token}`,
      "x-api-key": security_key,
      "Content-Type": "application/json",
    };

    setIsSaving(true);
    try {
      if (pageRecord?._id) {
        // EDIT
        await axios.put(`${FINANCING_API}/${pageRecord._id}`, payload, {
          headers,
        });
        showToast("Financing page updated successfully!");
      } else {
        // CREATE
        await axios.post(FINANCING_API, payload, { headers });
        showToast("Financing page created successfully!");
      }
      await fetchFinancingPage(); // reload saved data and return to view mode
    } catch (error) {
      console.error("Error saving financing page:", error);
      showToast(
        error.response?.data?.message ||
          "Failed to save financing page. Please try again."
      );
    } finally {
      setIsSaving(false);
    }
  };

  /* ════════════════════════════════════
     Reusable image picker block
  ════════════════════════════════════ */
  const renderImagePicker = (src, onCancel, type, label) => (
    <div className="banner-upload" style={{ marginTop: "8px" }}>
      {src ? (
        <div className="image-preview-wrapper">
          <img src={`${Url}${src}`} alt={label} className="image-preview" />
          {!isViewMode && (
            <button type="button" onClick={onCancel} className="cancel-button">
              X
            </button>
          )}
        </div>
      ) : isViewMode ? (
        <span className="upload-text">No image</span>
      ) : (
        <label
          className="upload-label"
          onClick={() => handleGalleryModalOpen(type)}
        >
          <div className="Catupload-button">
            <IoImageOutline size={35} color="#555" className="uploaded-image" />
          </div>
          <span className="upload-text">Click to Upload {label}</span>
        </label>
      )}
    </div>
  );

  /* ════════════════════════════════════
     Right sidebar
  ════════════════════════════════════ */
  const statusText = pageRecord ? "Saved" : "Not created";
  const modeText = isViewMode ? "Viewing" : isEditMode ? "Editing" : "Creating";

  const rightAccordionItems = [
    {
      title: "Status",
      content: (
        <div className="Blogs-PublishContainer">
          <div className="Blogs-StartContainer">
            {isViewMode ? (
              <CustomBtn
                label="Edit Page"
                withIcon={false}
                disabled={false}
                className="Blogs-PublishBtn"
                onClick={handleEdit}
                type="button"
              />
            ) : (
              <CustomBtn
                label={
                  isSaving ? (
                    <div className="btn-loader-Publish"></div>
                  ) : isEditMode ? (
                    "Update"
                  ) : (
                    "Create Page"
                  )
                }
                withIcon={false}
                disabled={isSaving}
                className="Blogs-PublishBtn"
                onClick={handleSave}
                type="button"
              />
            )}
          </div>

          <div className="Blogs-MidContainer">
            <div className="MidRowsData">
              <div className="RowContent">
                <img src={imageStatus} alt="Status" className="ImageStyle" />
                <span className="TextStyle1">Status:</span>
                <span className="TextStyle2">{statusText}</span>
              </div>
              <PiNotePencil className="NoteIcon" />
            </div>
            <div className="MidRowsData">
              <div className="RowContent">
                <img src={imageVisibility} alt="Mode" className="ImageStyle" />
                <span className="TextStyle1">Mode:</span>
                <span className="TextStyle2">{modeText}</span>
              </div>
              <PiNotePencil className="NoteIcon" />
            </div>
            <div className="MidRowsData">
              <div className="RowContent">
                <img src={imagePublish} alt="Updated" className="ImageStyle" />
                <span className="TextStyle1">Last updated:</span>
                <span className="TextStyle2">
                  {formatDate(pageRecord?.updatedAt)}
                </span>
              </div>
              <PiNotePencil className="NoteIcon" />
            </div>
          </div>

          {isEditMode && (
            <div className="Blogs-EndContainer">
              <CustomBtn
                label="Cancel Edit"
                withIcon={false}
                disabled={isSaving}
                className="CancelBtn"
                onClick={handleCancel}
                type="button"
              />
            </div>
          )}
        </div>
      ),
    },
  ];

  /* ════════════════════════════════════
     Render
  ════════════════════════════════════ */
  if (loading) {
    return (
      <div className="backdrop">
        <div className="loader-container">
          <MainLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="AddProductPage">
      {/* ══════════════ LEFT SIDE ══════════════ */}
      <div className="PageLeftSide blogs">
        <div className="BlogsDescription">
          {/* Content */}
          <div className="Blog-Container1-Row-2">
            <label htmlFor="editor" className="BlogContent-Heading">
              Page Content
            </label>
            <div className="Blogs-Editor-Container">
              <div className="Blogs-Editor-Tabs">
                <div
                  className={`Tab ${selectedTab === "Visual" ? "active" : ""}`}
                  onClick={() => setSelectedTab("Visual")}
                >
                  Visual
                </div>
                <div
                  className={`Tab ${selectedTab === "Text" ? "active" : ""}`}
                  onClick={() => setSelectedTab("Text")}
                >
                  Text
                </div>
              </div>
              <div className="Editor-Content">
                {selectedTab === "Visual" ? (
                  <JoditEditor
                    key={mode} // remount so readonly toggles correctly
                    ref={editor}
                    value={formData.content}
                    tabIndex={1}
                    onBlur={handleEditorChange}
                    config={editorConfig}
                  />
                ) : (
                  <textarea
                    id="content"
                    name="content"
                    value={formData.content}
                    readOnly={isViewMode}
                    onChange={handleInputChange}
                    className="Blog-Editor-Text-Area"
                    placeholder="<h1>Your page HTML here</h1>"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════ SEO ══════════════ */}
        <div className="SEO">
          <div className="SEO-Header">
            <label>SEO</label>
          </div>
          <div className="SEO-Body">
            <div className="SEO-Row-1">
              <div className="Row-1-LeftSide">Focus Key Phrase</div>
              <div className="Row-1-RightSide">
                <textarea
                  name="metaKeywords"
                  value={metaData.metaKeywords}
                  onChange={handleMetaChange}
                  className="SEOinput-01"
                  placeholder="furniture financing, no credit check"
                  disabled={isViewMode}
                />
              </div>
            </div>

            {SEO_ACCORDION_TITLES.map((title, index) => (
              <div key={title} className={`SEO-Row-${index + 2}`}>
                <div
                  onClick={() => toggleAccordion(index + 2)}
                  className="AccordionHeader"
                >
                  <span>{title}</span>
                  <div
                    className={`AccordionIcon ${
                      accordionsOpen[index + 2] ? "rotate" : ""
                    }`}
                  >
                    {accordionsOpen[index + 2] ? <FaChevronUp /> : <FaChevronDown />}
                  </div>
                </div>

                <div
                  className={`AccordionContent ${
                    accordionsOpen[index + 2] ? "open" : ""
                  }`}
                >
                  {/* ── Search Appearance ── */}
                  {title === "Search Appearance" && (
                    <>
                      <div className="ViewSelection">
                        <div className="CheckBtn">
                          <label
                            className={`MobileCheckLabel ${
                              isMobileAnimating && selectedView === "Mobile"
                                ? "MobileViewAnimate"
                                : ""
                            }`}
                          >
                            <input
                              type="radio"
                              name="seoViewOption"
                              value="Mobile"
                              checked={selectedView === "Mobile"}
                              onChange={() => handleViewChange("Mobile")}
                              className="MobileCheckBtn"
                            />
                            Mobile View
                          </label>
                          <label
                            className={`WebCheckLabel ${
                              isWebAnimating && selectedView === "Web"
                                ? "WebViewAnimate"
                                : ""
                            }`}
                          >
                            <input
                              type="radio"
                              name="seoViewOption"
                              value="Web"
                              checked={selectedView === "Web"}
                              onChange={() => handleViewChange("Web")}
                              className="WebCheckBtn"
                            />
                            Web View
                          </label>
                        </div>
                        <div className="ViewContent">
                          {selectedView === "Mobile" ? (
                            <div className="MobileViewContent">
                              <div className="Mobile-FirstRow">
                                <div className="MobileLogo">
                                  <img src={Logo} alt="Logo" />
                                </div>
                                <div className="MobileHeader">
                                  <span className="Header-Top">
                                    {metaData.metaTitle || "Page Title"}
                                  </span>
                                  <span className="Header-Bottom">
                                    myfurnituremecca.com
                                  </span>
                                </div>
                                <div className="MobileSelector">
                                  <img src={Selector} alt="Selector" />
                                </div>
                              </div>
                              <div className="Mobile-SecondRow">
                                <span className="Header-Secondrow">
                                  {metaData.metaTitle || "– Page Title"}
                                </span>
                                <span className="Para-Secondrow">
                                  {metaData.metaDescription ||
                                    "Please provide a meta description."}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="WebViewContent">
                              <div className="Web-FirstRow">
                                <div className="WebLogo">
                                  <img src={Logo} alt="Logo" />
                                </div>
                                <div className="WebHeader">
                                  <span className="Header-Top">
                                    {metaData.metaTitle || "Page Title"}
                                  </span>
                                  <span className="Header-Bottom">
                                    myfurnituremecca.com
                                  </span>
                                </div>
                                <div className="WebSelector">
                                  <img src={Selector} alt="Selector" />
                                </div>
                              </div>
                              <div className="Web-SecondRow">
                                <span className="Header2-Secondrow">
                                  {metaData.metaTitle || "– Page Title"}
                                </span>
                                <span className="Para2-Secondrow">
                                  {metaData.metaDescription ||
                                    "Please provide a meta description."}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="SEO-SearchAppearance SEO-Bordered">
                        <div className="Row-2-LeftSide">SEO Title</div>
                        <div className="Row-2-RightSide">
                          <textarea
                            name="metaTitle"
                            value={metaData.metaTitle}
                            onChange={handleMetaChange}
                            className="SEOinput-02"
                            disabled={isViewMode}
                          />
                        </div>
                      </div>

                      <div className="SEO-SearchAppearance SEO-Bordered">
                        <div className="Row-2-LeftSide">Canonical URL</div>
                        <div className="Row-2-RightSide">
                          <textarea
                            name="canonicalUrl"
                            value={metaData.canonicalUrl}
                            onChange={handleMetaChange}
                            className="SEOinput-03"
                            placeholder="https://myfurnituremecca.com/financing"
                            disabled={isViewMode}
                          />
                        </div>
                      </div>

                      <div className="SEO-SearchAppearance SEO-Bordered">
                        <div className="Row-2-LeftSide">Meta Description</div>
                        <div className="Row-2-RightSide">
                          <textarea
                            name="metaDescription"
                            value={metaData.metaDescription}
                            onChange={handleMetaChange}
                            className="SEOinput-04"
                            placeholder="Flexible furniture financing options"
                            disabled={isViewMode}
                          />
                        </div>
                      </div>

                      <div className="SEO-SearchAppearance">
                        <div className="Row-2-LeftSide">Robots</div>
                        <div className="Row-2-RightSide">
                          <textarea
                            name="robots"
                            value={metaData.robots}
                            onChange={handleMetaChange}
                            className="SEOinput-04"
                            placeholder="index,follow"
                            disabled={isViewMode}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* ── Open Graph ── */}
                  {title === "Open Graph (OG)" && (
                    <>
                      <div className="SEO-SearchAppearance SEO-Bordered">
                        <div className="Row-2-LeftSide">OG Title</div>
                        <div className="Row-2-RightSide">
                          <textarea
                            name="ogTitle"
                            value={metaData.ogTitle}
                            onChange={handleMetaChange}
                            className="SEOinput-02"
                            disabled={isViewMode}
                          />
                        </div>
                      </div>
                      <div className="SEO-SearchAppearance SEO-Bordered">
                        <div className="Row-2-LeftSide">OG Description</div>
                        <div className="Row-2-RightSide">
                          <textarea
                            name="ogDescription"
                            value={metaData.ogDescription}
                            onChange={handleMetaChange}
                            className="SEOinput-04"
                            disabled={isViewMode}
                          />
                        </div>
                      </div>
                      <div className="SEO-SearchAppearance">
                        <div className="Row-2-LeftSide">OG Image</div>
                        <div className="Row-2-RightSide">
                          {renderImagePicker(
                            selectedOgImage,
                            cancelOgImage,
                            "og-image",
                            "OG Image"
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* ── X Card ── */}
                  {title === "X (Twitter) Card" && (
                    <>
                      <div className="SEO-SearchAppearance SEO-Bordered">
                        <div className="Row-2-LeftSide">X Title</div>
                        <div className="Row-2-RightSide">
                          <textarea
                            name="xTitle"
                            value={metaData.xTitle}
                            onChange={handleMetaChange}
                            className="SEOinput-02"
                            disabled={isViewMode}
                          />
                        </div>
                      </div>
                      <div className="SEO-SearchAppearance SEO-Bordered">
                        <div className="Row-2-LeftSide">X Description</div>
                        <div className="Row-2-RightSide">
                          <textarea
                            name="xDescription"
                            value={metaData.xDescription}
                            onChange={handleMetaChange}
                            className="SEOinput-04"
                            disabled={isViewMode}
                          />
                        </div>
                      </div>
                      <div className="SEO-SearchAppearance">
                        <div className="Row-2-LeftSide">X Image</div>
                        <div className="Row-2-RightSide">
                          {renderImagePicker(
                            selectedXImage,
                            cancelXImage,
                            "x-image",
                            "X Image"
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* ── Schema ── */}
                  {title === "Schema Markup" && (
                    <div className="SEO-SearchAppearance">
                      <div className="Row-2-LeftSide">JSON-LD Schema</div>
                      <div className="Row-2-RightSide">
                        <textarea
                          name="schemaMarkup"
                          value={metaData.schemaMarkup}
                          onChange={handleMetaChange}
                          className="SEOinput-04"
                          rows={6}
                          style={{ minHeight: "120px", fontFamily: "monospace" }}
                          placeholder={`{\n  "@context": "https://schema.org",\n  "@type": "WebPage"\n}`}
                          disabled={isViewMode}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══════════════ RIGHT SIDE ══════════════ */}
      <div className="PageRightSide blogs">
        {rightAccordionItems.map((item) => (
          <AccordionItem
            key={item.title}
            title={item.title}
            content={item.content}
            defaultOpen={true}
            maxHeight="2000px"
          />
        ))}
      </div>

      {/* Shared gallery popup for editor / OG / X images */}
      <ImageGalleryPopup
        showImageGalleryPopUp={modalView}
        handleModalView={handleGalleryModalClose}
        handleFileChange={handleFileChange}
        onImageSelect={handleImageSelect}
        imageSendPayload={imageSendPayload}
        setImageSendPayload={setImageSendPayload}
        alt_text={imageSendPayload.alt_text}
        title={imageSendPayload.title}
        data={modaldata}
        getData={getApi}
      />

      <BottomToust showMessage={showMessage} message={toastMessage} />
    </div>
  );
};

export default AddEditMainFinancingPage;